// frontend/src/components/community/MessagesPanel.js
import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, User, MoreHorizontal, Phone, Search, X, Store, Package, Briefcase, ShoppingBag, Check, CheckCheck, MessageSquare } from 'lucide-react';
import MessageInput from './MessageInput';
import useCommunityStore from '../../store/communityStore';
import useAuthStore from '../../store/authStore';

const MessagesPanel = ({ 
  selectedConversation, 
  onBack,
  externalMessages = null,
  externalSendMessage = null,
  externalIsLoading = false
}) => {
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [showMenu, setShowMenu] = useState(false);
  
  const communityStore = useCommunityStore();
  const { user } = useAuthStore();

  const messages = externalMessages !== null ? externalMessages : (communityStore.messages || []);
  const isLoading = externalMessages !== null ? externalIsLoading : communityStore.isLoading;
  const sendMessage = externalSendMessage || communityStore.sendMessage;

  useEffect(() => {
    if (selectedConversation && !externalSendMessage) {
      loadMessages();
    }
  }, [selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadMessages = async () => {
    if (!selectedConversation?.user?.id) return;
    try {
      await communityStore.getMessages(selectedConversation.user.id, {
        businessId: selectedConversation.businessContext?.id
      });
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const handleSendMessage = async (content) => {
    if (!content.trim() || !selectedConversation?.user?.id) return;
    try {
      await sendMessage({
        receiverId: selectedConversation.user.id,
        content,
        businessId: selectedConversation.businessContext?.id,
        businessName: selectedConversation.businessContext?.name
      });
      scrollToBottom(true);
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  const scrollToBottom = (force = false) => {
    if (!messagesEndRef.current) return;
    if (force) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    const container = messagesContainerRef.current;
    if (container) {
      const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 200;
      if (isNearBottom) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const formatMessageDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const getContextIcon = () => {
    if (!selectedConversation) return null;
    if (selectedConversation.productContext) return Package;
    if (selectedConversation.serviceContext) return Briefcase;
    if (selectedConversation.orderContext) return ShoppingBag;
    if (selectedConversation.businessContext) return Store;
    return null;
  };

  const ContextIcon = getContextIcon();

  if (!selectedConversation) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-white">
        <div className="text-center px-6">
          <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-3">
            <MessageSquare className="h-8 w-8 text-blue-500" />
          </div>
          <h3 className="text-base font-semibold text-gray-900 mb-0.5">Select a conversation</h3>
          <p className="text-xs text-gray-500">Choose from your existing conversations</p>
        </div>
      </div>
    );
  }

  const otherUser = selectedConversation.user;
  const isBusiness = selectedConversation.type === 'business';

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header - Compact */}
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm border-b border-gray-100">
        <div className="px-3 py-2 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <button
              onClick={onBack}
              className="lg:hidden p-1 -ml-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 text-gray-700" />
            </button>
            
            <button
              onClick={() => {
                if (otherUser?.id) {
                  window.location.href = `/community/profile/${otherUser.id}`;
                }
              }}
              className="flex items-center space-x-2 group cursor-pointer hover:bg-gray-50 rounded-lg p-1 transition-colors"
            >
              <div className="relative">
                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
                  {otherUser?.avatar ? (
                    <img 
                      src={otherUser.avatar} 
                      alt={otherUser?.firstName || 'User'}
                      className="h-8 w-8 rounded-full object-cover"
                    />
                  ) : (
                    <span className="text-white font-semibold text-xs">
                      {otherUser?.firstName?.[0] || '?'}{otherUser?.lastName?.[0] || ''}
                    </span>
                  )}
                </div>
                
                {isBusiness && (
                  <div className="absolute -bottom-0.5 -right-0.5 bg-blue-500 rounded-full p-0.5 ring-1 ring-white">
                    <Store className="h-2 w-2 text-white" />
                  </div>
                )}
              </div>
              
              <div className="text-left">
                <div className="flex items-center space-x-1.5">
                  <h3 className="font-semibold text-gray-900 text-sm group-hover:text-primary-600 transition-colors">
                    {otherUser?.firstName || 'User'} {otherUser?.lastName || ''}
                  </h3>
                  {isBusiness && selectedConversation.businessContext && (
                    <span className="text-[8px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full">
                      Business
                    </span>
                  )}
                </div>
                <p className="text-[10px] text-gray-500 group-hover:text-gray-700 transition-colors">
                  @{otherUser?.firstName?.toLowerCase() || 'user'}{otherUser?.lastName?.toLowerCase() || ''}
                </p>
              </div>
            </button>
          </div>
        </div>
        
        {/* Business Context Banner - Compact */}
        {isBusiness && selectedConversation.businessContext && (
          <div className="px-3 pb-2">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-2 border border-blue-200">
              <div className="flex items-start space-x-2">
                <div className="p-1.5 bg-blue-100 rounded">
                  {ContextIcon ? (
                    <ContextIcon className="h-3 w-3 text-blue-600" />
                  ) : (
                    <Store className="h-3 w-3 text-blue-600" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-800 truncate">
                    {selectedConversation.businessContext.name}
                  </p>
                  <p className="text-[10px] text-gray-600 mt-0.5 truncate">
                    {selectedConversation.productContext && `Product: ${selectedConversation.productContext.name}`}
                    {selectedConversation.serviceContext && `Service: ${selectedConversation.serviceContext.title}`}
                    {selectedConversation.orderContext && `Order #${selectedConversation.orderContext.orderNumber}`}
                    {!selectedConversation.productContext && !selectedConversation.serviceContext && !selectedConversation.orderContext && 
                      'Business inquiry'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Messages Area */}
      <div 
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto px-3 pb-2"
        style={{ scrollBehavior: 'smooth' }}
      >
        {isLoading && messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className={`flex w-full ${i % 2 === 0 ? 'justify-end' : 'justify-start'}`}>
                <div className={`h-8 w-40 rounded-2xl animate-pulse ${
                  i % 2 === 0 ? 'bg-primary-100' : 'bg-gray-100'
                }`} />
              </div>
            ))}
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-3">
              <MessageSquare className="h-7 w-7 text-gray-400" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900 mb-0.5">No messages yet</h3>
            <p className="text-xs text-gray-500">
              Start the conversation with {otherUser?.firstName || 'them'}
            </p>
            {isBusiness && selectedConversation?.businessContext && (
              <p className="text-[10px] text-gray-400 mt-1.5">
                They typically respond within 24 hours
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-3 py-1.5">
            {/* Group messages by date */}
            {(() => {
              const groups = [];
              let currentDate = null;
              
              messages.forEach((message) => {
                const messageDate = new Date(message.createdAt).toDateString();
                if (messageDate !== currentDate) {
                  currentDate = messageDate;
                  groups.push({ date: messageDate, messages: [] });
                }
                groups[groups.length - 1].messages.push(message);
              });
              
              return groups.map((group, idx) => (
                <div key={idx}>
                  <div className="flex justify-center my-2">
                    <span className="text-[9px] text-gray-500 bg-gray-50 px-2 py-0.5 rounded-full">
                      {new Date(group.date).toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    {group.messages.map((message) => {
                      const isSender = message.sender?.id === user?.id;
                      const hasBusinessContext = message.businessId || message.productId || message.serviceId;
                      
                      return (
                        <div key={message.id} className={`flex ${isSender ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[75%] ${isSender ? 'items-end' : 'items-start'} flex flex-col`}>
                            <div
                              className={`px-3 py-1.5 rounded-2xl ${
                                isSender
                                  ? 'bg-primary-500 text-white rounded-br-sm'
                                  : 'bg-gray-100 text-gray-900 rounded-bl-sm'
                              }`}
                            >
                              {!isSender && hasBusinessContext && (
                                <div className="flex items-center space-x-0.5 mb-0.5">
                                  <Store className="h-2.5 w-2.5 text-blue-500" />
                                  <span className="text-[8px] text-blue-600 font-medium">
                                    Business Inquiry
                                  </span>
                                </div>
                              )}
                              <p className="text-xs leading-relaxed break-words">
                                {message.content}
                              </p>
                            </div>
                            <div className="flex items-center space-x-0.5 mt-0.5 px-1">
                              <span className="text-[8px] text-gray-400">
                                {formatMessageDate(message.createdAt)}
                              </span>
                              {isSender && (
                                <>
                                  {message.read ? (
                                    <div className="flex items-center">
                                      <CheckCheck className="h-2.5 w-2.5 text-blue-500" />
                                    </div>
                                  ) : (
                                    <Check className="h-2.5 w-2.5 text-gray-400" />
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ));
            })()}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Message Input */}
      <MessageInput 
        onSendMessage={handleSendMessage}
        placeholder={isBusiness ? "Message the business owner..." : "Type your message..."}
      />
    </div>
  );
};

export default MessagesPanel;